import type { useCreateTradeForm, SearchInventoryItem } from '../../hooks/useCreateTradeForm';
import { AMENDABLE_FORM_FIELDS } from '@/services/trades/amend-mapper';

// Extract types from the hook's return value
type HookReturn = ReturnType<typeof useCreateTradeForm>;
export type TradeForm = HookReturn['form'];
export type CalculatedValues = HookReturn['calculatedValues'];

export interface SectionState {
  securityDetails: boolean;
  accountDetails: boolean;
  positionDetails: boolean;
  nonCashCollateral: boolean;
  rateDetails: boolean;
  positionDates: boolean;
  settlementDetails: boolean;
  tradeSummary: boolean;
}

export const inputClassName = 'w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500';
export const labelClassName = 'block text-xs text-gray-500 mb-1';
export const readOnlyInputClassName = 'w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-50 text-gray-600';

// LR-578 (review kvi #1): marks the Gross Price input so the async COB prefill
// can tell whether the operator is currently inside that field. DecimalInput
// deliberately skips its external-value sync while focused (LR-371: don't
// collapse "0.000" → "0" mid-typing), so writing the form value under a focused
// input would change what gets booked without changing what is displayed — and
// Enter submits straight from the focused input, without a blur. Lives here
// because both the section that renders the input and the prefill hook import
// this module.
export const GROSS_PRICE_FIELD_MARKER = 'gross-price';

// LR-484 (amend): the create-trade sections are reused by AmendTradeModal in
// "amend mode". Amend may change a deal's NUMBERS but not its CONDITIONS, so every
// field NOT in this set is locked when `amendMode` is on (default-deny — a field
// added later is locked in amend until it is added to the registry).
// LR-495: the set is now DERIVED from the single AMEND_FIELDS registry in
// services/trades/amend-mapper.ts (re-exported here under the legacy name the
// sections import). An amendable field is one edit to that registry, plus the
// backend PositionUpdateSerializer.Meta.fields whitelist (the cross-language half,
// pinned by a contract test). `settlement_date` is the form field for the open
// settlement date.
/** Re-exported under the name the sections import; derived from the registry. */
export const AMENDABLE_FIELDS = AMENDABLE_FORM_FIELDS;

/**
 * True when a field must be locked in amend mode. In create mode (`amendMode`
 * undefined/false) this is always false, so wrapping an input's `disabled` with
 * `|| isAmendLocked(amendMode, name)` is a no-op for the create form by construction.
 */
export const isAmendLocked = (amendMode: boolean | undefined, field: string): boolean =>
  !!amendMode && !AMENDABLE_FIELDS.has(field);

export type { SearchInventoryItem };
